import { useContext, useState } from 'react';

import { useToast } from '@chakra-ui/react';
import { t } from '@lingui/macro';
import cloneDeep from 'lodash.clonedeep';
import { useEffectOnce, useUpdateEffect } from 'usehooks-ts';

import { apiGetAvailabilitiesDetails, ROUTE_P2_AVA_SELECTION, ROUTE_P1_PRODUCT_SELECTION, LOCAL_STORAGE_CART_ID } from '../constants';
import { CoreContext } from '../context/CoreContext';
import { devMessage } from '../settings/settings';
import { isArrEmpty } from '../utils/arrayUtil';
import { getUniqueSessionGroupIds } from '../utils/business/availabilities';
import { getMainPrice } from '../utils/business/prices';
import useNavigateBookingEngine from './useNavigateBookingEngine';
import usePost from './usePost';
import { pushStandardDataInfo, STANDARD_ADDITIONAL_DATA } from './useReadAdditionalData';
import useReadLoggedUser from './useReadLoggedUser';
import useReadStorageBookingEngine from './useReadStorageBookingEngine';
import useReadTeam from './useReadTeam';
import useWritePreCart from './useWritePreCart';

const usePostLoadVariant = (availabilities, queryProduct) => {
  const toast = useToast();

  const loggedUser = useReadLoggedUser();
  const { teamId } = useReadTeam();
  const { coreData } = useContext(CoreContext);
  const { sell_options: canSellOptions } = coreData.booking_engine;

  const { preCartData, setPreCartDefaultVariant } = useWritePreCart();
  const { itemsToDeleteForEdit } = preCartData;

  const [formattedData, setFormattedData] = useState(null);
  const { data, status, executePost } = usePost(apiGetAvailabilitiesDetails(), formattedData);

  const storedCartId = useReadStorageBookingEngine(LOCAL_STORAGE_CART_ID);

  const navigate = useNavigateBookingEngine();

  useEffectOnce(() => {
    devMessage('Going to load variants for AVA:', availabilities, 'queryProduct?', queryProduct);
    if (isArrEmpty(availabilities)) return;

    // On edit we already loaded everything
    if (itemsToDeleteForEdit) return;

    const allSessionGroupIds = getAvailabilitiesDetailRequestData(availabilities, teamId, queryProduct, storedCartId);
    setFormattedData(allSessionGroupIds);
  });

  useUpdateEffect(() => {
    executePost();
  }, [JSON.stringify(formattedData)]);

  useUpdateEffect(() => {
    // When react reload state
    if (!data) return;

    if (!data.success) {
      showToast(data.message);
      const productId = preCartData.product?.id;
      navigate(productId ? `${ROUTE_P2_AVA_SELECTION}/${productId}` : ROUTE_P1_PRODUCT_SELECTION);
      return;
    }

    // We tried to access P3 directly so we wanted to queryProduct as well (because it's not in storage)
    // But if for some reason the backend doesnt give the product then we move to P1
    const hasProductInData = 'product' in data;
    if (!hasProductInData && queryProduct) {
      navigate(ROUTE_P1_PRODUCT_SELECTION);
      return;
    }

    const accessControlPartner = hasProductInData ? data['product'].access_control_partner : preCartData.product.access_control_partner;

    setPreCartDefaultVariant(
      createDefaultVariant(data, loggedUser, canSellOptions, accessControlPartner, availabilities[0].session_group_id),
      loggedUser,
      hasProductInData ? data['product'] : null
    );
  }, [data]);

  // For example a selected price_variation is not available anymore so the backend sends a 4xx
  const reloadAfterError = errorMessage => {
    // During edit reloading etc... is too complicated, it's a rare case so we redirect to the product
    if (itemsToDeleteForEdit) {
      const productId = preCartData.product?.id;
      navigate(productId ? `${ROUTE_P2_AVA_SELECTION}/${productId}` : ROUTE_P1_PRODUCT_SELECTION);
      showToast(t`The product has changed since you added it to your cart, please try again.`, 'info', 8000);
    } else if (errorMessage !== null) {
      showToast(errorMessage, 'error', 6000);
    }
    // On normal P3 we just reload the correct data
    else {
      executePost();
      showToast(t`The product was updated during your action, please try again.`, 'info', 8000);
    }
  };

  const showToast = (message, type, duration) => {
    if (!message || toast.isActive('P3_ERROR')) return;

    toast({
      id: 'P3_ERROR',
      title: message,
      status: type ?? 'error',
      duration: duration ?? 4000,
      variant: 'subtle',
    });
  };

  return { status, itemsToDeleteForEdit, reloadAfterError };
};
export default usePostLoadVariant;

// This default variant belong to a selected availability
// Each time we add a new variant, this one will be cloned
export const createDefaultVariant = (availabilityDetail, loggedUser, canSellOptions, accessControlPartner, relatedSessionGroupId) => {
  const mainPriceData = getMainPrice(availabilityDetail.standard_price, 1, availabilityDetail.prices);

  let participantDetails = loggedUser ? null : getParticipantDetails(availabilityDetail.client_data, accessControlPartner);
  // if we're logged there's no participant info in P3 but we still want to do the access control check
  if (loggedUser && accessControlPartner != null) {
    participantDetails = getParticipantDetails({ participant_details_required: false }, accessControlPartner);
  }

  const defaultVariant = {
    participant: 1,
    currentPriceVariationId: mainPriceData.priceVariationId,
    standardPrice: availabilityDetail.standard_price,
    standardPriceWithoutPromo: availabilityDetail.standard_price_without_promo,
    canSelectPrice: canSelectPrice(availabilityDetail),
    canAddVariant: canAddVariant(availabilityDetail, loggedUser),
    participantDetails: participantDetails,
    hasAccessControl: accessControlPartner != null,
    nbSessions: availabilityDetail.nb_sessions,
    start_date: availabilityDetail.start_date ?? null,
    relatedSessionGroupId: relatedSessionGroupId, // very annoying to have that but it's for FFV license check P3, it cannot be reliable in multiselect
  };
  defaultVariant.custom_options = canSellOptions === false ? [] : formatCustomOptions(availabilityDetail.options);
  defaultVariant.custom_prices = cloneDeep(availabilityDetail.prices);
  defaultVariant.cart_rule_data = {
    cart_rule_id: availabilityDetail.cart_rule_id,
    cart_rule_condition_item_ids: availabilityDetail.cart_rule_condition_item_ids,
  };
  defaultVariant.marketing_rule_data = {
    marketing_rule_id: availabilityDetail.marketing_rule_id,
  };
  return defaultVariant;
};

const canSelectPrice = availabilityDetail => {
  return availabilityDetail && availabilityDetail.prices.length > 0;
};

// When logged we dont need to add variants if no prices and no options (the variants would be the same)
// When unlogged we always need the button because we cannot manually enter the participants number
const canAddVariant = (availabilityDetail, loggedUser) => {
  if (loggedUser) {
    return (availabilityDetail && availabilityDetail.options.length > 0) || canSelectPrice(availabilityDetail);
  }
  return true;
};

const formatCustomOptions = options => {
  const copyOptions = cloneDeep(options);
  // uncomment if we decide to order alphabetically
  // copyOptions.sort((a, b) => (a.label.length > b.label.length ? 1 : -1));

  // A required option with a single resource is auto-selected (no 'None' choice shown).
  // We preselect it in the data so it's always sent to the backend, instead of relying on
  // the Select's mount onChange which can be lost when several auto-selects happen at once.
  for (const option of copyOptions) {
    if (option.required && option.resources && option.resources.length === 1) {
      option.selectedResource = option.resources[0].id;
    }
  }
  return copyOptions;
};

const getParticipantDetails = (clientDataNeeded, accessControlPartner) => {
  const standardData = [];
  pushStandardDataInfo(standardData, clientDataNeeded, accessControlPartner, ...STANDARD_ADDITIONAL_DATA);

  // even if participant_details_required is false, we can have standardData fill when product has access control enabled
  if (!clientDataNeeded.participant_details_required && isArrEmpty(standardData)) {
    // so we only return null when we're sure there will be nothing
    return null;
  }

  const customData = getCustomData(clientDataNeeded.specific_data);

  return {
    additionalDataNeeded: {
      standardData: standardData,
      customData: customData,
    },
    hasAdditionalDataNeeded: standardData.length > 0 || customData.length > 0,
    client: { extra_data: {} },
  };
};

const getCustomData = extraData => {
  const customData = [];
  if (extraData == null) return customData;

  for (const data of extraData) {
    customData.push({ ...data.specific_person_data, required: data.required });
  }
  customData.sort((a, b) => a.name.localeCompare(b.name));
  return customData;
};

export const getAvailabilitiesDetailRequestData = (availabilities, teamId, queryProduct, storedCartId) => {
  devMessage('Will get session detail, queryProduct?', queryProduct, 'cart?', storedCartId);
  return {
    session_group_ids: getUniqueSessionGroupIds(availabilities),
    team_id: teamId,
    query_product: queryProduct,
    order_id: storedCartId,
  };
};
